/**
 * How many seats one hold request or one cart request may carry, and which
 * seats count toward the cap.
 *
 * Seats at a table booked as a unit (tableConfig.bookingMode 'whole-table', or
 * 'variable' with its minimum) do not count: a table is bounded by the table
 * itself, and a twelve-top has to be held in one request. Only single seats
 * are capped. Every request is still bounded by an absolute ceiling.
 *
 * The hold route (POST /api/events/[id]/seats/hold) has applied this rule
 * since 4e99d3e, but the cart (POST /api/cart/items) kept a flat 10 seats per
 * request. So two six-seat VIP tables held fine (12 seats, all exempt) and the
 * cart then refused them with "Maximum 10 seats per request", leaving both
 * tables HELD and off sale for the length of the hold. The hold, the cart and
 * the seat picker now read the rule from here.
 */

/** Absolute ceiling on the seats in one request, tables included. */
export const MAX_SEATS_PER_REQUEST = 60

/** Single seats (not part of a table booked as a unit) per cart request. */
export const MAX_SINGLE_SEATS_PER_REQUEST = 10

/** A section's seats are booked as a unit: every booking mode except by-seat. */
export function isUnitBookingMode(bookingMode: string | null | undefined): boolean {
  return !!bookingMode && bookingMode !== 'by-seat'
}

/** The keys of the chart sections whose seats are booked as a unit. */
export function unitSectionKeys(
  sections: Array<{ key: string; tableConfig?: { bookingMode?: string | null } | null }> | null | undefined
): Set<string> {
  return new Set(
    (sections || []).filter(s => isUnitBookingMode(s.tableConfig?.bookingMode)).map(s => s.key)
  )
}

/**
 * How many of these seats count toward the single-seat cap: every seat that
 * is not in a unit section. A seat whose section is unknown counts.
 */
export function countSingleSeats(
  seatIds: string[],
  sectionKeyOf: (seatId: string) => string | null | undefined,
  unitKeys: Set<string>
): number {
  return seatIds.filter(id => {
    const key = sectionKeyOf(id)
    return !key || !unitKeys.has(key)
  }).length
}
